import React from "react";
import { NavigateFunction } from "react-router-dom";
import { FormFieldConfig } from "Components/Common/CommonForm";
import { Column } from "Components/Common/CommonTableIndexWise";
import { Button } from "reactstrap";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  BloodGroup_OPTIONS,
  Gender_OPTIONS,
  MedicalCode_OPTIONS,
  PrimaryLanguage_OPTIONS,
  FoodHabit_OPTIONS,
  AlcoholResult_OPTIONS,
  SMOKING_OPTIONS,
  EducationLevel_OPTIONS,
} from "api/modules/common.option";
import { formatDateTimeIST } from "./crf.utils";
import { STUDY_PATHS } from "Routes/paths";
import { CommonOption } from "api/modules/common.option";
import { validations } from "validations/FormValidations";
import { isValid, parseISO, format } from "date-fns";
import Commonbadge from "Components/Common/Commonbadge";
import { formatDate } from "common/service/CommonService";

dayjs.extend(customParseFormat);

/* --------------------------------
 Common Types
--------------------------------- */

export type AnyObject = Record<string, unknown>;

export interface TableColumn<T = AnyObject> {
  key?: string;
  label: string;
  type?: string;
  width?: string;
  textCenter?: boolean;
  render?: (
    value: unknown,
    row: T,
    index: number,
    allRows: T[],
  ) => React.ReactNode;
  onClick?: (row: T) => void;
  cell?: (row: T) => React.ReactNode;
}

/* --------------------------------
 Utils
--------------------------------- */

export const formatDDMMYYYY = (date?: string | Date): string => {
  if (!date) return "";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

/* --------------------------------
 LocalStorage helpers
--------------------------------- */

export const getVoluteerName = (): string => {
  try {
    const s = JSON.parse(sessionStorage.getItem("CURRENT_VOLUNTEER") || "");
    return s?.fullname;
  } catch {
    return "Volunteer Details";
  }
};

/* --------------------------------
CRF Section 
--------------------------------- */

export const getLeftOffset = (columns: any, index: any) =>
  columns
    .slice(0, index)
    .filter((col: any) => col.sticky)
    .reduce((sum: any, col: any) => {
      const w = typeof col.width === "number" ? col.width : parseInt(col.width, 10) || 0;
      return sum + w;
    }, 0);

/* ================================
   VolunteerList 
================================ */

export const volunteerColumn = (handlePrint: (row: any) => void) => [
  { key: "fullname", label: "Name" },
  { key: "gender", label: "Gender" },
  { key: "age", label: "Age" },
  { key: "mobilenum", label: "Mobile" },
  { key: "email", label: "Email" },

  {
    key: "barcode",
    label: "Barcode",
    cell: (row: any) => (
      <Button
        color="primary"
        outline
        size="sm"
        className="rounded-pill px-3 d-flex align-items-center gap-2"
        onClick={() => handlePrint(row)}
      >
        <i className="ri-printer-line"></i>
        Print Barcode
      </Button>
    ),
  },
];

/* ================================
   Adverse Event 
================================ */


export const isPreviewableFile = (file?: unknown): file is File => {
  return (
    file instanceof File &&
    ["image/jpeg", "image/png", "image/jpg", "application/pdf"].includes(
      file.type,
    )
  );
};


export const calculateExpiryDate = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + Number(days));
  return d.toISOString().split("T")[0];
};


export const parseLocalDate = (value: Date | string | null | undefined): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string") {
    const cleanStr = value.trim();
    // YYYY-MM-DD
    const yyyymmddMatch = cleanStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (yyyymmddMatch) {
      const parsed = dayjs(yyyymmddMatch[0], "YYYY-MM-DD");
      return parsed.isValid() ? parsed.toDate() : null;
    }
    // DD-MM-YYYY
    const ddmmyyyyMatch = cleanStr.match(/^(\d{2})-(\d{2})-(\d{4})/);
    if (ddmmyyyyMatch) {
      const parsed = dayjs(ddmmyyyyMatch[0], "DD-MM-YYYY");
      return parsed.isValid() ? parsed.toDate() : null;
    }
    const parsed = dayjs(cleanStr);
    return parsed.isValid() ? parsed.toDate() : null;
  }
  return null;
};

export const formatDateTimeLocal = (value?: Date | string) => {
  if (!value) return "-";

  // Ensure proper UTC parsing
  const d =
    value instanceof Date
      ? value
      : new Date(value.includes("T") ? value : value.replace(" ", "T") + "Z");

  if (isNaN(d.getTime())) return "-";

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();

  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");

  return `${dd}-${mm}-${yyyy} (${hh}:${min}:${ss})`;
};



export const studyColumn = (navigate: NavigateFunction, isModuleVisible?: any): Column[] => {
  const showStudyDetails = typeof isModuleVisible === "function" ? isModuleVisible("CLINICAL_DETAILS") : true;
  const showStudyLiteDetails = typeof isModuleVisible === "function" ? isModuleVisible("CLINICAL_LITE_STUDY_DETAILS") : true;

  return [
    {
      key: "studycode",
      label: "Study Code",
      type: "link",
      onClick: (row) => {
        sessionStorage.setItem("activestudyid", row.studyid);
        sessionStorage.setItem("studycode", row.studycode);
        if (showStudyDetails) {
          navigate("/study/detail");
        } else if (showStudyLiteDetails) {
          navigate("/study/detail/studylite");
        }
      },
    },
    ...(showStudyDetails
      ? [
          { key: "studytitle", label: "Study Title" },
          { key: "sponsorname", label: "Sponsor" },
          { key: "studydesigntext", label: "Design" },
        ]
      : []),
    { key: "totalsubjects", label: "Subjects" },
    { key: "numberofperiods", label: "Periods" },
    ...(showStudyLiteDetails
      ? [
          { key: "numberofaliquots", label: "Aliquot" },
          { key: "totalsamplingpoints", label: "Sampling Point" },
        ]
      : []),
  ];
};

export const StudyICFFields: FormFieldConfig[] = [
  {
    name: "icfversion",
    label: "Version of ICF",
    type: "number",
    placeholder: "Enter Version of ICF",
    required: true,
    rules: validations.checklist.versionno,
  },
  {
    name: "language",
    label: "Language",
    type: "select",
    options: PrimaryLanguage_OPTIONS,
    required: true,
  },
  {
    name: "signeddate",
    label: "Signed Date",
    type: "date",
    required: true,
    optionsProps: {
      maxDate: new Date(),
    }
  },
];

export const studySections = [
  {
    id: 1,
    title: "Study Identification",
    icon: "ri-file-text-line",
    keys: ["studytitle", "studycode", "versionnumber", "protocolversiondate"],
  },
  {
    id: 2,
    title: "Sponsor & CRO Details",
    icon: "ri-building-line",
    keys: ["sponsorname", "croname"],
  },
  {
    id: 3,
    title: "Regulatory & Ethics",
    icon: "ri-shield-check-line",
    keys: ["ethicscommitteename", "ethicsapprovaldate", "regulatoryauthority"],
  },
  {
    id: 4,
    title: "Study Design",
    icon: "ri-layout-grid-line",
    keys: [
      "studytypetext",
      "numberoftreatments",
      "numberofperiods",
      "studydesign",
      "numberofaliquots",
      "numberofsequences",
      "fastingcondition",
    ],
  },
  {
    id: 5,
    title: "Checklist Assignment",
    icon: "ri-checkbox-multiple-line",
    keys: [],
  },
  {
    id: 6,
    title: "CRF Assignment",
    icon: "ri-task-line",
    keys: [],
  },
  {
    id: 7,
    title: "Subject Details",
    icon: "ri-user-line",
    keys: ["totalsubjects", "extrasubjects", "gender", "agemin", "agemax", "bmimin", "bmimax"],
  },
  {
    id: 8,
    title: "Investigational Product",
    icon: "ri-medicine-bottle-line",
    keys: [
      "testdrugname",
      "testdosageform",
      "teststrength",
      "testbatchno",
      "testmanufacturer",
      "testmanufacturingdate",
      "testexpirydate",
      "refdrugname",
      "refdosageform",
      "refstrength",
      "refbatchno",
      "refmanufacturer",
      "refmanufacturingdate",
      "refexpirydate",
    ],
  },
  {
    id: 9,
    title: "Study Timeline",
    icon: "ri-calendar-event-line",
    keys: ["screeningdate", "screeningvalidity", "washoutdays", "periods"],
  },
  {
    id: 10,
    title: "Dosing Details",
    icon: "ri-capsule-line",
    keys: [
      "dosestrength",
      "routeofadministration",
      "dosingtime",
      "watervolume",
      "foodrestrictions",
      "lightcondition",
      "subjectinterval",
      "predosewindowminutes",
    ],
  },
  {
    id: 11,
    title: "Sample Collection",
    icon: "ri-test-tube-line",
    keys: [
      "biologicalmatrix",
      "totalsamplingpoints",
      "sampleprocessingmethod",
      "storagetemperature",
      "samplingpoints",
    ],
  },
  {
    id: 12,
    title: "Ambulatory Visit",
    icon: "ri-walk-line",
    keys: [
      "visitname",
      "visittype",
      "visitcategory",
      "visitdescription",
      "scheduleddate",
      "visitstartday",
      "visitendday",
    ],
  },
];

export const getActiveStudy = () =>
  JSON.parse(sessionStorage.getItem("activeStudy") || "{}");

export const getStudyName = () =>
  sessionStorage.getItem("studyName") || "Study Details";

export const siteColumns = () => [
  { key: "sitename", label: "Site Name" },
  { key: "sitecode", label: "Site Code" },
  { key: "sitelocation", label: "Location" },
  { key: "sitetype", label: "Type" },
];

export const calculateExpiryFromRecord = (
  createddate: string,
  validityDays: number,
): string => {
  if (!createddate || !validityDays) return "";

  const date = new Date(createddate); // server date
  if (isNaN(date.getTime())) return "";

  date.setDate(date.getDate() + Number(validityDays));

  return date.toISOString().split("T")[0]; // YYYY-MM-DD
};



/* --------------------------------
 Volunteer Filter Modal
--------------------------------- */

export const filterFields: FormFieldConfig[] = [
  {
    name: "gender",
    label: "Gender",
    type: "select",
    options: Gender_OPTIONS,
  },
  { name: "minAge", label: "Min Age", type: "number" },
  { name: "maxAge", label: "Max Age", type: "number" },

  { name: "minbmi", label: "Min BMI", type: "number" },
  { name: "maxbmi", label: "Max BMI", type: "number" },

  {
    name: "isSmoker",
    label: "Smoker",
    type: "select",
    options: SMOKING_OPTIONS,
  },

  {
    name: "alcohol",
    label: "Alcohol Consumption",
    type: "select",
    options: AlcoholResult_OPTIONS,
  },

  {
    name: "bloodgroup",
    label: "Blood Group",
    type: "select",
    options: BloodGroup_OPTIONS,
  },

  {
    name: "educationlevel",
    label: "Education Level",
    type: "select",
    options: EducationLevel_OPTIONS,
  },

  {
    name: "foodhabit",
    label: "Food Habit",
    type: "select",
    options: FoodHabit_OPTIONS,
  },

  {
    name: "city",
    label: "City",
    type: "text",
  },

  {
    name: "screeningPassed",
    label: "Screening Passed",
    type: "select",
    options: MedicalCode_OPTIONS,
  },

  { name: "screeningFromDate", label: "Screening From Date", type: "date" },
  { name: "screeningToDate", label: "Screening To Date", type: "date" },
];

/* -----------------------------
     COLUMNS
  ----------------------------- */

export const subjectEnrollmentColumns = (
  handleAddClick: (subjectNo: number, row: any) => void,
  handleEditClick: (row: any) => void,
  handleVolunteerNameClick: (row: any) => void,
  handleBarcodeClick: (row: any) => void,
  selectedIds: number[],
  handleSelectAll: (isChecked: boolean) => void,
  isAllSelected: boolean,
  canadd?: boolean,
  handleViewReplacementClick?: (row: any) => void,
): Column[] => [
    {
      key: "barcode",
      label: "Barcode",
      type: "link",
      onClick: (row: any) => row?.volunteerid && handleBarcodeClick(row),
      render: (value: any, row: any) =>
        value !== null && value !== undefined && String(value).trim() !== "" ? (
          <span className={row?.volunteerid ? "text-primary fw-semibold text-decoration-none" : "text-muted"} title="Open Subject Info">{value}</span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: "subjectno",
      label: "SubjectNo",
      render: (subjectNo: number, row: any) => {
        const hasReplacement = !!(
          row?.replacementstatus === 'replaced'
        );
        return (
          <div className="d-flex align-items-center gap-2">
            <span>{String(row?.subjectno || subjectNo).padStart(2, "0")}</span>
            {canadd && row?.volunteerid === null && (
              // ➕ ADD
              <i
                className="ri-user-add-line text-primary fs-5 cursor-pointer"
                title="Assign Volunteer"
                onClick={() => handleAddClick(subjectNo, row)}
              ></i>
            )}
            {hasReplacement && handleViewReplacementClick && (
              // ℹ️ INFO
              <i
                className="ri-user-shared-line text-danger text-info fs-5 cursor-pointer"
                title="View Replacement Info"
                onClick={() => handleViewReplacementClick(row)}
              ></i>
            )}
          </div>
        );
      },
    },
    {
      key: "initials",
      label: "Subject Initials",
      textCenter: true,
      render: (row: any, data: any) => {
        if (!row) {
          return <span className="text-muted">--</span>;
        }

        return (
          <span
            className="text-primary cursor-pointer"
            onClick={() => handleVolunteerNameClick(data)}
          >
            {row}
          </span>
        );
      },
    },
    {
      key:"subjectstudystatus",
      label:"Status",
      textCenter: true,
      render: (row: any,) => {
        if (!row) {
          return <span className="text-muted">--</span>;
        }
        return (
          <Commonbadge status={row} style={{ fontSize:"12px" }} />
        );
      },
    },
  ];

/* -----------------------------
     for printlog model
  ----------------------------- */
export const printLogColumns = [
  // {
  //   key: "index",
  //   label: "#",
  // },
  {
    key: "printbyname",
    label: "Print By",
    sticky: true,
  },
  {
    key: "printdate",
    label: "Print Date",
    sticky: true,
    render(value: any) {
      return formatDateTimeIST(value);
    },
  },
];

// Volunteer Study Screening List Tabs

export const STATUS_TABS = [
  { id: "screening_pending", label: "Ongoing", icon: "ri-time-line" },
  { id: "screening_passed", label: "Passed", icon: "ri-checkbox-circle-line" },
  { id: "screening_failed", label: "Failed", icon: "ri-close-circle-line" },
  // new tab for transfer
  { id: "transferred", label: "Transferred", icon: "ri-arrow-left-right-line" },
];


export const getAllSubjectForScreening = (navigate: any): Column[] => [
  {
    key: "initials",
    label: "Subject initials",
    type: "link",
    onClick: (row: any) => {
      // debugger
      sessionStorage.setItem(
        "activevolunteerid",
        JSON.stringify(row.volunteerid),
      );
      sessionStorage.setItem("activevolunteer", JSON.stringify(row));
      sessionStorage.setItem(
        "active_volunteer_screening_status",
        JSON.stringify(row.assignmentstatus),
      );
      sessionStorage.setItem("volunteername", row.fullname || "");
      sessionStorage.setItem(
        "volunteerassignmentid",
        row.volunteerassignmentid || "",
      );
      navigate(STUDY_PATHS.SCREENING);
    },
  },
  { key: "fullname", label: "Name" },
  { key: "gendertext", label: "Gender" },
  { key: "age", label: "Age" },
  { key: "mobilenum", label: "Mobile" },
  { key: "email", label: "Email" },
];






// Study Screening List constant End
/* -----------------------------
     for Workflow
----------------------------- */
export const workflowStatusColumns = (
  canAssign: boolean,
  selectedStudyId?: number | null,
  selectedPharmacyId?: number | null,
  handleToggleClick?: (row: any, type: "study" | "pharmacy") => void,
): Column[] => [
    {
      label: "Workflow Name",
      key: "name",
      tooltip: true,
      maxLength: 30,
    },
    {
      label: "Stages",
      key: "total_stages",
      cell: (row) => row.total_stages || 0,
    },
    {
      label: "Last Updated",
      key: "updateddate",
      render(value) {
        return formatDate(value);
      },
    },
    ...(canAssign
      ? [
        {
          key: "assign",
          label: "Assign to Module",
          textCenter: true,
          cell: (row: any) => {
            const isStudy = selectedStudyId === row.workflowid;
            const isPharmacy = selectedPharmacyId === row.workflowid;

            return (
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  justifyContent: "center",
                }}
              >
                {/* STUDY */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    cursor: "pointer",
                    padding: "4px 10px",
                    margin: 0,
                    borderRadius: "20px",
                    border: "1px solid #ced4da",
                    background: isStudy ? "#E1F5EE" : "#fff",
                    color: isStudy ? "#085041" : "#555",
                    fontSize: "12px",
                    fontWeight: isStudy ? 600 : 400,
                    transition: "all 0.2s",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isStudy}
                    onChange={() => handleToggleClick?.(row, "study")}
                    style={{ cursor: "pointer", margin: 0 }}
                  />
                  Study
                </label>

                {/* PHARMACY */}
                {/* <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                  padding: "4px 10px",
                  margin: 0,
                  borderRadius: "20px",
                  border: "1px solid #ced4da",
                  background: isPharmacy ? "#E6F1FB" : "#fff",
                  color: isPharmacy ? "#0C447C" : "#555",
                  fontSize: "12px",
                  fontWeight: isPharmacy ? 600 : 400,
                  transition: "all 0.2s",
                }}
              >
                <input
                  type="checkbox"
                  checked={isPharmacy}
                  onChange={() => handleToggleClick?.(row, "pharmacy")}
                  style={{ cursor: "pointer", margin: 0 }}
                />
                Pharmacy
              </label> */}
              </div>
            );
          },
        },
      ]
      : []),
  ];

const createDateTime = (date: string, time: string) => {
  return new Date(`${date} ${time}`);
};

export const AddDoshingformFields: FormFieldConfig[] = [
  {
    name: "dosescheduledate",
    label: "Schedule Date",
    type: "flatpickr",
    placeholder: "Select date",
    required: true,
    rules: { required: "Schedule Date is required" },
  },
  {
    name: "dosetime",
    label: "Dose Time",
    type: "time",
    disabled: true,
    required: true,
    rules: { required: "Dose Time is required" },
  },
  {
    name: "dosevolume",
    label: "Dose Volume",
    type: "text",
    placeholder: "e.g. 10 mL",
    required: true,
    rules: { required: "Dose Volume is required" },
  },
  {
    name: "actualdosetime",
    label: "Actual Schedule Time",
    type: "time",
    required: true,
    rules: {
      validate: (value: string, formValues: any) => {
        if (!formValues.dosetime) return true;
        return value >= formValues.dosetime || "Must be ≥ Dose Time";
      },
    },
  },
  {
    name: "predosetime",
    label: "Predose Time",
    type: "time",
    required: true,
    rules: {
      required: "Predose Time is required",
    },
  },
];

export const getLabelFromValue = (
  options: CommonOption[],
  value?: number | string | null,
  emptyText = "—"
): string => {
  if (value === undefined || value === null) return emptyText;
  const numericValue = typeof value === "string" ? parseInt(value, 10) : value;
  return options.find(opt => opt.value === numericValue)?.label ?? emptyText;
};


export const normalizeDOB = (dob: any): string => {
  if (!dob) return "";

  // ✅ Date object → YYYY-MM-DD (LOCAL, no UTC)
  if (dob instanceof Date && !isNaN(dob.getTime())) {
    const day = String(dob.getDate()).padStart(2, "0");
    const month = String(dob.getMonth() + 1).padStart(2, "0");
    const year = dob.getFullYear();

    return `${year}-${month}-${day}`;
  }

  if (typeof dob === "string") {
    // ✅ Already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
      return dob;
    }

    // ✅ Handle DD-MM-YYYY  ← THIS WAS MISSING
    if (/^\d{2}-\d{2}-\d{4}$/.test(dob)) {
      const [day, month, year] = dob.split("-");
      return `${year}-${month}-${day}`;
    }
  }

  return "";
};



export const normalizeFormValues = (values: any, parentKey?: string): any => {
  // Array
  if (Array.isArray(values)) {
    return values.map((item) => normalizeFormValues(item));
  }

  // Empty
  if (values === "" || values === undefined) {
    return null;
  }

  // ✅ Handle Date object
  if (values instanceof Date) {
    return format(values, "yyyy-MM-dd");
  }

  // ✅ Handle string (STRICT)
  if (typeof values === "string") {
    const trimmed = values.trim();

    // ✅ Only parse if it matches ISO pattern
    const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}/;

    if (ISO_DATE_REGEX.test(trimmed)) {
      const parsedDate = parseISO(trimmed);

      if (isValid(parsedDate)) {
        return format(parsedDate, "yyyy-MM-dd");
      }
    }

    return trimmed; // "85" stays "85" ✅
  }

  // Object (recursive)
  if (typeof values === "object" && values !== null) {
    const normalized: any = {};
    Object.entries(values).forEach(([key, value]) => {
      normalized[key] = normalizeFormValues(value);
    });
    return normalized;
  }

  return values;
};



